import { useRef, useState, DragEvent, ChangeEvent, KeyboardEvent } from 'react';
import { Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';

import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import {
  ACCEPTED_FILE_EXTENSIONS,
  FILE_INPUT_ACCEPT,
  MAX_FILE_SIZE_LABEL
} from './upload_validation';

interface FileDropZoneProps {
  /** Called with the raw picked/dropped files; the parent validates them. */
  onFilesSelected: (files: Array<File>) => void;
  /** Disable interaction (e.g. while an upload is in flight). */
  disabled?: boolean;
}

/**
 * Drag-drop target + click-to-pick file selector for the Honey upload flow.
 * Shows the accepted formats and the per-file size limit; validation itself
 * is the parent's responsibility (and ultimately the server's).
 * @param {(files: Array<File>) => void} onFilesSelected Receives the raw selection.
 * @param {boolean | undefined} disabled Disable interaction.
 * @returns {JSX.Element}
 */
export default function FileDropZone({ onFilesSelected, disabled }: FileDropZoneProps) {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);

  const emitFiles = (file_list: FileList | null): void => {
    if (!file_list || file_list.length === 0) {
      return;
    }
    onFilesSelected(Array.from(file_list));
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragActive(false);
    if (disabled) {
      return;
    }
    emitFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (!disabled) {
      setDragActive(true);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    emitFiles(event.target.files);
    // Reset so re-picking the same file fires another change event.
    event.target.value = '';
  };

  const openFilePicker = (): void => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={openFilePicker}
      onKeyDown={handleKeyDown}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={() => setDragActive(false)}
      sx={{
        border: `2px dashed ${dragActive ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: 2,
        p: 4,
        textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer',
        backgroundColor: dragActive ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
        opacity: disabled ? 0.5 : 1,
        transition: 'border-color 0.15s, background-color 0.15s'
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={FILE_INPUT_ACCEPT}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
        data-testid="syntiro-honey-file-input"
      />
      <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
      <BodyText weight="Medium">Drag and drop files here, or click to browse</BodyText>
      <CaptionText sx={{ mt: 1, display: 'block' }}>
        {`Accepted: ${ACCEPTED_FILE_EXTENSIONS.join(', ')} — max ${MAX_FILE_SIZE_LABEL} per file. Zip archives are expanded by the server.`}
      </CaptionText>
    </Box>
  );
}
